/**
 * @fileoverview Button visibility utility for computing disabled/hidden state
 * @module screen-view-v2/utils/buttonVisibility
 *
 * Implements visibility logic for footer buttons based on:
 * - ButtonVisibility enum from v2 schema (default, disabledUntilValueEntered, hiddenUntilValueEntered)
 * - hasValue flag (whether user has entered a value)
 * - hasValidationError flag (for email validation special case)
 *
 * Key v1 → v2 change:
 * - Button → TapTarget
 * - (button?.styles as any)?.visibility → tapTarget?.appearance?.visibility (no cast needed)
 */

import type { TapTarget, ButtonVisibility } from '@segmently/schema-models/v2';

/**
 * Input for computing button visibility state
 */
export interface ButtonVisibilityInput {
  /** Visibility setting from TapTargetStyle.visibility */
  visibility: ButtonVisibility | string | undefined;
  /** Whether user has entered a value (non-empty field, selected item, etc.) */
  hasValue: boolean;
  /** Whether there's a validation error (e.g., invalid email) */
  hasValidationError?: boolean;
}

/**
 * Output state for button rendering
 */
export interface ButtonVisibilityState {
  /** Whether button should be disabled (visible but not clickable) */
  disabled: boolean;
  /** Whether button should be hidden (not rendered) */
  hidden: boolean;
}

/**
 * Compute button disabled/hidden state based on visibility setting.
 *
 * Visibility behavior:
 * - `default` (or undefined): Button always visible and enabled (unless validation error)
 * - `disabledUntilValueEntered`: Button visible but disabled until value entered
 * - `hiddenUntilValueEntered`: Button hidden until value entered, then visible
 *
 * Special case: hasValidationError always disables button (for email validation)
 *
 * @param input - visibility setting, hasValue flag, optional validation error
 * @returns { disabled, hidden } state for the button
 *
 * @example
 * getButtonVisibilityState({ visibility: 'default', hasValue: false })
 * // => { disabled: false, hidden: false }
 *
 * @example
 * getButtonVisibilityState({ visibility: 'disabledUntilValueEntered', hasValue: false })
 * // => { disabled: true, hidden: false }
 */
export function getButtonVisibilityState(input: ButtonVisibilityInput): ButtonVisibilityState {
  const { visibility, hasValue, hasValidationError = false } = input;

  // Default state: visible and enabled
  let disabled = false;
  let hidden = false;

  // Handle visibility setting
  switch (visibility) {
    case 'disabledUntilValueEntered':
      // Disabled until user enters a value
      disabled = !hasValue;
      break;

    case 'hiddenUntilValueEntered':
      // Hidden until user enters a value
      hidden = !hasValue;
      break;

    case 'default':
    case undefined:
    default:
      // Default: always visible and enabled (no restrictions)
      break;
  }

  // Validation error disables button (email special case)
  // Only apply when button is visible (not hidden)
  // Hidden button doesn't need disabled state
  if (hasValidationError && !hidden) {
    disabled = true;
  }

  return { disabled, hidden };
}

/**
 * Extract visibility state from TapTarget schema and value state.
 * Convenience function for use in screen components.
 *
 * V2 improvement: TapTargetStyle explicitly has `visibility` field,
 * so no `as any` cast is needed (unlike v1 Button).
 *
 * @param tapTarget - TapTarget schema object (may be undefined)
 * @param hasValue - Whether user has entered a value
 * @param hasValidationError - Whether there's a validation error (email)
 * @returns { disabled, hidden } state for the button
 *
 * @example
 * const button1State = getButtonStateFromSchema(
 *   content.actionBar?.primaryAction,
 *   fieldValue.trim().length > 0,
 *   fieldError !== null
 * );
 */
export function getButtonStateFromSchema(
  tapTarget: TapTarget | undefined,
  hasValue: boolean,
  hasValidationError?: boolean
): ButtonVisibilityState {
  const visibility = tapTarget?.appearance?.visibility;

  return getButtonVisibilityState({
    visibility,
    hasValue,
    hasValidationError,
  });
}
